import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  TaggedGroup,
  TaggingValidationSuccess,
  TaggingValidationSuccessType,
  TagItemVendorService,
  Vendor as TagVendor,
} from '../tag-item-vendor.service';
import {
  IAddItem,
  MyPurchaseForm,
} from 'src/app/shared/models/app-models/MyPurchaseForm.model';
import { Subject, takeUntil } from 'rxjs';
import { UserInformation } from 'src/app/shared/models/app-models';
import { Store } from '@ngrx/store';
import { Vendor } from 'src/app/shared/models/database-models';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import {
  SourcingStatusId,
  SourcingStatusOption,
  SourcingStatusOptions,
} from 'src/app/shared/models/database-models/SourcingStatus';
import _ from 'lodash';

export interface TaggingUpdateDialogData {
  existingTaggedGroupRef: TaggedGroup;
}

type ItemNumbers = IAddItem['displayItemNumber'][];
type NonNullVendorNumbers = NonNullable<Vendor['VendorNumber']>[];

enum HasVendorTag {
  YES = 'yes',
  NO = 'no',
}

@Component({
  selector: 'app-tagging-update',
  templateUrl: './tagging-update.component.html',
  styleUrls: ['./tagging-update.component.scss'],
})
export class TaggingUpdateComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();
  form: MyPurchaseForm | null = null;
  public loggedInUsername?: string;
  public sourcingStatusOptions = _.cloneDeep(SourcingStatusOptions);

  constructor(
    public dialogRef: MatDialogRef<TaggingUpdateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TaggingUpdateDialogData,
    private store: Store<{ user: UserInformation; form: MyPurchaseForm }>,
    public tagItemVendorService: TagItemVendorService
  ) {
    this.store
      .select('user')
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: UserInformation) => {
        this.loggedInUsername = user.userName;
      });

    this.store
      .select('form')
      .pipe(takeUntil(this.destroy$))
      .subscribe((form: MyPurchaseForm) => {
        this.form = form;
      });

    this.tagItemVendorService.taggingValidationSuccess$
      .pipe(takeUntil(this.destroy$))
      .subscribe((validationSuccess: TaggingValidationSuccess) => {
        if (
          validationSuccess.type === TaggingValidationSuccessType.PASSED_UPDATE
        ) {
          // Close this dialog
          this.dialogRef.close();
          this.tagItemVendorService.resetTempTagGroup();
        }
      });
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  onDialogAction() {
    // Update taggedGroups with tempTagGroup
    const tempTagGroup = this.tagItemVendorService.tempTagGroupValue!;

    // Asynchronously resolve validation issues and subscribe to handle validationSuccess
    const { isSuccess } = this.tagItemVendorService.updateTaggedGroup(
      this.data.existingTaggedGroupRef,
      tempTagGroup
    );
  }

  public onItemTableSelectedChange(itemNumbers: ItemNumbers) {
    this.tagItemVendorService.updateTempTagGroup({
      itemNumbers: itemNumbers,
    });
  }

  public onHasVendorTagChange(event: MatButtonToggleChange) {
    const value = event.value;

    if (value === HasVendorTag.YES) {
      this.tagItemVendorService.updateTempTagGroup({
        vendors: [],
        sourcingStatusId: SourcingStatusId.POTENTIAL_VENDOR_IDENTIFIED,
      });
    } else if (value === HasVendorTag.NO) {
      this.tagItemVendorService.updateTempTagGroup({
        vendors: null,
        sourcingStatusId: undefined,
      });
    }
  }

  public onVendorTableSelectedChange(vendorNumbers: NonNullVendorNumbers) {
    const vendors = this.form?.VendorsToSource.filter(
      (vendor) =>
        vendor.VendorNumber &&
        !vendor.isDeleted &&
        !vendor.Strikethrough &&
        vendorNumbers.includes(vendor.VendorNumber)
    ).map((vendor) => ({
      vendorName: vendor.Name ?? '',
      vendorNumber: vendor.VendorNumber!,
    }));

    this.tagItemVendorService.updateTempTagGroup({
      vendors: vendors,
    });
  }

  public mapVendorElement(
    vendors: TaggedGroup['vendors'],
    element: keyof TagVendor
  ) {
    return vendors?.map((vendor) => vendor[element]) ?? [];
  }

  public getFilterItemNumbers() {
    // Filter to itemNumbers which current user is the PurchaseExecutive of
    if (this.form?.ItemList && this.loggedInUsername) {
      const itemNumbersForCurrentPE = this.form.ItemList.filter(
        (item) => item.AddItems.PurchaseExecutive === this.loggedInUsername
      ).map((item) => item.AddItems.displayItemNumber);

      return itemNumbersForCurrentPE;
    }

    return [];
  }

  public onSourcingStatusChange(option: SourcingStatusOption) {
    this.tagItemVendorService.updateTempTagGroup({
      sourcingStatusId: option.id,
    });
  }

  public get HasVendorTag(): typeof HasVendorTag {
    return HasVendorTag;
  }

  public get SourcingStatusId(): typeof SourcingStatusId {
    return SourcingStatusId;
  }
}
